import type { DropdownData } from '@fitogest/reactive-form-builder/interfaces';
import { setDropdownSelected } from '@fitogest/reactive-form-builder/utils';
import { ReactiveCatalogSource } from '@fitogest/reactive-form-builder/catalog';
import type { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import type { ParcelasEnvironmentDefinition } from '../environment-variables';
import {
    PARCELAS_SIEX_CATALOG_SOURCE,
    type ParcelasDropdownDataSourceFactory,
    type SourceAwareParcelasCatalogRow
} from './parcelas-dropdown-datasource.factory';

export type ParcelasCatalogHydrationContext = {
    readonly previousData: ParcelasModel;
    readonly env: ParcelasEnvironmentDefinition;
    readonly formType: number;
    readonly cultivosDropdown: DropdownData<unknown>;
    readonly variedadesDropdown: DropdownData<unknown>;
    readonly sectoresDropdown?: DropdownData<unknown>;
    readonly sistemasRiegoDropdown?: DropdownData<unknown>;
    readonly dataSourceFactory: ParcelasDropdownDataSourceFactory;
};

/**
 * Hydrates legacy Parcelas dropdown values into temporary display options.
 *
 * Legacy persisted rows can contain plain SIEX/local ids while the declarative dropdowns now work with
 * canonical values such as `siex:122` or `propio:77:3`. This adapter keeps that compatibility layer out of
 * the visual component and also seeds temporary dropdown options so edit forms can display saved values before
 * the network catalog finishes loading. It also seeds dependent dropdowns such as sector and irrigation system
 * because their catalogs can load after the edit/duplicate value has already been patched into the form.
 */
export class ParcelasCatalogHydrator {

    public static transformPreviousData(context: ParcelasCatalogHydrationContext): ParcelasModel {
        const transformedData = { ...context.previousData } as Record<string, unknown>;

        this.seedPreviousPrimitiveSelections(context, transformedData);

        if (!context.env.activateApiSIEX) {
            return transformedData as ParcelasModel;
        }

        const currentCultivo = transformedData['id_cultivo'];
        const currentVariedad = transformedData['id_variedad'];
        const parsedCultivo = context.dataSourceFactory.parseCatalogKey(currentCultivo);
        const source = parsedCultivo.source ?? PARCELAS_SIEX_CATALOG_SOURCE;
        const cultivoCode = context.dataSourceFactory.resolvePreviousCatalogCode(
            parsedCultivo.primaryCode,
            currentCultivo,
            transformedData['cultivo'],
        );

        if (cultivoCode !== '' && !parsedCultivo.source) {
            transformedData['id_cultivo'] = context.dataSourceFactory.buildCatalogKey(source, cultivoCode);
        }

        const parsedVariedad = context.dataSourceFactory.parseCatalogKey(currentVariedad);
        const variedadCode = context.dataSourceFactory.resolvePreviousCatalogCode(
            parsedVariedad.secondaryCode,
            currentVariedad,
            transformedData['id_variedad'],
            transformedData['codigo_variedad'],
            transformedData['variedad'],
        );

        if (cultivoCode !== '' && variedadCode !== '' && !parsedVariedad.source) {
            transformedData['id_variedad'] = context.dataSourceFactory.buildCatalogKey(source, cultivoCode, variedadCode);
        }

        this.seedPreviousCatalogSelections(
            context,
            transformedData,
            transformedData['id_cultivo'],
            transformedData['id_variedad'],
        );

        return transformedData as ParcelasModel;
    }

    private static seedPreviousCatalogSelections(
        context: ParcelasCatalogHydrationContext,
        previousData: Record<string, unknown>,
        cultivoValue: unknown,
        variedadValue: unknown,
    ): void {
        const cultivoKey = context.dataSourceFactory.toStringId(cultivoValue);

        if (cultivoKey !== '') {
            const cultivoRaw = this.buildPreviousCultivoOptionRaw(context, previousData, cultivoKey);
            const cultivoOption = context.dataSourceFactory.mapCultivoDropdownOption(cultivoRaw);

            this.markDropdownDataAsHydrationSeed(context.cultivosDropdown);
            ReactiveCatalogSource.upsertDropdownOption(context.cultivosDropdown, cultivoOption);
            setDropdownSelected(context.cultivosDropdown, cultivoRaw);
        }

        const variedadKey = context.dataSourceFactory.toStringId(variedadValue);

        if (variedadKey !== '') {
            const variedadRaw = this.buildPreviousVariedadOptionRaw(context, previousData, cultivoKey, variedadKey);
            const variedadOption = context.dataSourceFactory.mapVariedadDropdownOption(variedadRaw);

            this.markDropdownDataAsHydrationSeed(context.variedadesDropdown);
            ReactiveCatalogSource.upsertDropdownOption(context.variedadesDropdown, variedadOption);
            setDropdownSelected(context.variedadesDropdown, variedadRaw);
        }
    }

    private static seedPreviousPrimitiveSelections(
        context: ParcelasCatalogHydrationContext,
        previousData: Record<string, unknown>,
    ): void {
        this.seedPreviousSectorSelection(context, previousData);
        this.seedPreviousSistemaRiegoSelection(context, previousData);
    }

    private static seedPreviousSectorSelection(
        context: ParcelasCatalogHydrationContext,
        previousData: Record<string, unknown>,
    ): void {
        const id = context.dataSourceFactory.resolveFirstCatalogValue(previousData['id_sector'], previousData['sector']);

        if (id === '' || !context.sectoresDropdown) {
            return;
        }

        const label = context.dataSourceFactory.resolveFirstCatalogValue(
            previousData['sector'],
            previousData['sector_nombre'],
            previousData['nombre_sector'],
            id,
        );
        const raw = {
            ...previousData,
            id,
            id_sector: id,
            id_finca: context.dataSourceFactory.resolveFirstCatalogValue(previousData['id_finca'], previousData['finca']),
            nombre: label,
        };

        this.markDropdownDataAsHydrationSeed(context.sectoresDropdown);
        ReactiveCatalogSource.upsertDropdownOption(context.sectoresDropdown, {
            label,
            value: id,
            raw,
            valueAliases: this.buildPrimitiveAliases(
                id,
                previousData['sector'],
                previousData['sector_nombre'],
                previousData['nombre_sector'],
            ),
        });
        setDropdownSelected(context.sectoresDropdown, raw);
    }

    private static seedPreviousSistemaRiegoSelection(
        context: ParcelasCatalogHydrationContext,
        previousData: Record<string, unknown>,
    ): void {
        const codigo = context.dataSourceFactory.resolveFirstCatalogValue(
            previousData['codigo_sistema_riego'],
            previousData['codigo_SIEX_sistema_riego'],
            previousData['sistema_riego'],
        );

        if (codigo === '' || !context.sistemasRiegoDropdown) {
            return;
        }

        const label = context.dataSourceFactory.resolveFirstCatalogValue(
            previousData['sistema_riego'],
            previousData['sistema_de_riego'],
            previousData['nombre_sistema_riego'],
            codigo,
        );
        const raw = {
            ...previousData,
            codigo_SIEX: codigo,
            sistema_de_riego: label,
        };

        this.markDropdownDataAsHydrationSeed(context.sistemasRiegoDropdown);
        ReactiveCatalogSource.upsertDropdownOption(context.sistemasRiegoDropdown, {
            label,
            value: codigo,
            raw,
            valueAliases: this.buildPrimitiveAliases(
                codigo,
                previousData['sistema_riego'],
                previousData['sistema_de_riego'],
                previousData['nombre_sistema_riego'],
            ),
        });
        setDropdownSelected(context.sistemasRiegoDropdown, raw);
    }

    private static buildPrimitiveAliases(...values: readonly unknown[]): string[] {
        return values
            .map((value) => ReactiveCatalogSource.toStringId(value))
            .filter((value, index, aliases) => value !== '' && aliases.indexOf(value) === index);
    }

    private static markDropdownDataAsHydrationSeed(data: DropdownData<unknown>): void {
        data.provenance = 'hydrationSeed';
    }

    private static buildPreviousCultivoOptionRaw(
        context: ParcelasCatalogHydrationContext,
        previousData: Record<string, unknown>,
        catalogKey: string,
    ): SourceAwareParcelasCatalogRow {
        const parsedKey = context.dataSourceFactory.parseCatalogKey(catalogKey);
        const source = parsedKey.source ?? PARCELAS_SIEX_CATALOG_SOURCE;
        const catalogCode = context.dataSourceFactory.resolveFirstCatalogValue(
            parsedKey.primaryCode,
            previousData['id_cultivo'],
            previousData['cultivo'],
        );
        const cultivo = context.dataSourceFactory.resolveFirstCatalogValue(
            previousData['cultivo'],
            previousData['cultivo_nombre'],
            previousData['nombre_cultivo'],
            catalogCode,
        );

        return {
            ...previousData,
            id: catalogCode,
            id_cultivo: catalogCode,
            codigo: catalogCode,
            codigo_cultivo: catalogCode,
            cultivo,
            source,
            catalogKey,
            catalogCode,
        } as unknown as SourceAwareParcelasCatalogRow;
    }

    private static buildPreviousVariedadOptionRaw(
        context: ParcelasCatalogHydrationContext,
        previousData: Record<string, unknown>,
        cultivoKey: string,
        catalogKey: string,
    ): SourceAwareParcelasCatalogRow {
        const parsedCultivo = context.dataSourceFactory.parseCatalogKey(cultivoKey);
        const parsedVariedad = context.dataSourceFactory.parseCatalogKey(catalogKey);
        const source = parsedVariedad.source ?? parsedCultivo.source ?? PARCELAS_SIEX_CATALOG_SOURCE;
        const cultivoCode = context.dataSourceFactory.resolveFirstCatalogValue(
            parsedVariedad.primaryCode,
            parsedCultivo.primaryCode,
            previousData['id_cultivo'],
            previousData['cultivo'],
        );
        const variedadCode = context.dataSourceFactory.resolveFirstCatalogValue(
            parsedVariedad.secondaryCode,
            previousData['id_variedad'],
            previousData['variedad'],
        );
        const cultivo = context.dataSourceFactory.resolveFirstCatalogValue(
            previousData['cultivo'],
            previousData['cultivo_nombre'],
            previousData['nombre_cultivo'],
            cultivoCode,
        );
        const variedad = context.dataSourceFactory.resolveFirstCatalogValue(
            previousData['variedad'],
            previousData['variedad_nombre'],
            previousData['nombre_variedad'],
            variedadCode,
        );

        return {
            ...previousData,
            id: variedadCode,
            id_cultivo: cultivoCode,
            id_variedad: variedadCode,
            codigo: cultivoCode,
            codigo_cultivo: cultivoCode,
            codigo_variedad: variedadCode,
            cultivo,
            variedad,
            source,
            catalogKey,
            catalogCode: variedadCode,
        } as unknown as SourceAwareParcelasCatalogRow;
    }
}
